import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getBookingConfig, putBookingConfig, PutBookingConfigBody } from '../../../api/admin/bookingConfig.api';
import { selectCollAndDeliveryPointID } from '../../../auth/selectors/authSelectors';
import FormCard from '../../../components/Core/FormCard/FormCard';
import MainSection from '../../../components/Core/MainSection/MainSection';
import { TOAST_SEVERITY } from '../../../components/Core/Toast/Toast';
import { useSnackbar } from '../../../context/SnackbarContext';
import useBuildForm from '../../../hook/useBuildForm';
import useBookingForm, { transformRawBookingConfig } from './bookingConfig.form';

function BookingConfigPage() {
  const { t } = useTranslation('form');
  const snackbar = useSnackbar();
  const { id_coll } = useSelector(selectCollAndDeliveryPointID);
  const queryClient = useQueryClient();

  const { data: rawConfigBooking } = useQuery(
    ['get.bookingConfig', id_coll],
    ({ queryKey }) => {
      const [, id_coll] = queryKey;
      return getBookingConfig(id_coll as number);
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 40000_000,
    }
  );
  const { mutate: handleUpdateBookingConfig, isLoading } = useMutation(
    ['post.bookingConfig'],
    (data: Omit<PutBookingConfigBody, 'id_coll'>) => putBookingConfig({ ...data, id_coll }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['get.bookingConfig']);
        snackbar?.setAlert({ message: t('global_update_success_message'), severity: TOAST_SEVERITY.success });
      },
    }
  );
  const bookingConfig = useMemo(() => {
    return transformRawBookingConfig(rawConfigBooking?.data);
  }, [rawConfigBooking]);

  const bookingConfigForm = useBookingForm(rawConfigBooking?.data.paymentsMeans, bookingConfig?.options);
  const { builtFormFields, handleSubmit } = useBuildForm({
    formObject: bookingConfigForm,
    defaultValues: bookingConfig?.defaultValues,
    withValidation: false,
  });

  const onSubmit = useCallback(
    handleSubmit((data) => {
      handleUpdateBookingConfig(data as Omit<PutBookingConfigBody, 'id_coll'>);
    }),
    [handleSubmit]
  );

  return (
    <MainSection className={'reset'}>
      <FormCard onSubmit={onSubmit} isLoading={isLoading}>
        {builtFormFields}
      </FormCard>
    </MainSection>
  );
}

export default BookingConfigPage;
